// =============================================================================
// ui/tabs/CoachReport.tsx — Spot it.
// -----------------------------------------------------------------------------
// The first section of the Coach's Report, and the one every user sees — the old
// client's footer says Free shows Spot it and Pro unlocks the rest.
//
// ── 🚨 TWO LEDGERS, AND THE SUBHEADINGS ARE THE ARGUMENT ──
//
// "Money you lost" is booked and gone. "Money you left" is a counterfactual and
// an upper bound. They are never ranked against each other, and each ledger says
// what kind of number it holds in its own words — because a reader who takes the
// second for the first concludes they are down far more than they are.
//
// ── 🚨 A ROW WITH NO NUMBER SAYS WHICH KIND OF SILENCE IT IS ──
//
// Three different nothings, and they are not interchangeable:
//
//   "No leak here this period."     measured, and clean
//   "Not enough data yet…"          measurable, but the sample is too thin
//   "This client can't measure…"    not measurable here at all
//
// Collapsing them into one blank is how a trader concludes they have no problem
// when the truth is nobody looked.
//
// ⚠ THE PARAGRAPH THAT USED TO SIT HERE SAID THE FIX-IT ACTIONS WERE NOT BUILT
// AND THAT NO ROW RENDERS A BUTTON. Both halves are false — the rules lifecycle
// shipped, and `LeakRow` renders "Fix it →" and a "● Rule active" chip. A
// comment describing code that no longer exists is the trap this repo keeps
// finding in its own docs, so it is corrected rather than left to mislead.
//
// ── 🚨 WHAT IS FREE ON THIS TAB, AND WHAT IS NOT ──
//
// Spot it is free — a trader has to be able to SEE what their habits cost. Fix
// it, Prove it, the banked-habits ledger and the four lenses are Pro, and they
// are withheld by SECTION, not by refusing clicks: see `CoachProLock.tsx`. The
// action gate below is then the second line of defence, not the first.
// =============================================================================

import { useCallback, useMemo, useState } from 'react';
import { useAccounts } from '../state/accounts';
import { useContracts } from '../state/contracts';
import { usePreferences } from '../state/preferences';
import { useLeakReport } from '../state/useLeakReport';
import { useLeakRules } from '../state/useLeakRules';
import { money, moneyPlain, num } from '../format';
import { FixDialog, FixIt } from './CoachFixIt';
import { ProveIt } from './CoachProveIt';
import { CoachLedger } from './CoachLedger';
import { CoachDrill, type DrillTrade } from './CoachDrill';
import { EduMark } from '../components/EduMark';
import { CoachVerdict } from './CoachVerdict';
import { CoachDimensions, type DimensionData } from './CoachDimensions';
import { usePatterns } from '../state/patterns';
import { useProAction } from '../state/useProAction';
import { useEntitlement } from '../state/useEntitlement';
import {
  CoachDimensionsLock, CoachProLock, CoachSectionChecking,
} from './CoachProLock';
import { ProActionLimit } from '../shell/ProActionLimit';
import {
  PRO_ACTION_LABEL, PRO_ACTION_PITCH, proSectionLocked,
} from '../../selectors/proAction';
import { useExitModels } from '../state/exitModels';
import { mfeCapture } from '../../selectors/capture';
import { confluenceRegistry } from '../../selectors/confluences';
import { confluenceMarkReader } from '../../selectors/confluenceEdge';
import {
  confluenceRanking, dailyPnl, edgeQuality, pairings, riskOfRuin, seededRandom,
} from '../../selectors/coachDimensions';
import { useAiVerdict } from '../state/useAiVerdict';
import {
  buildVerdictPayload, verdictGate, type VerdictAccount,
} from '../../selectors/aiVerdict';
import {
  COACH_WINDOWS, DEFAULT_COACH_WINDOW, coachHeadline, coachPriorWindow,
  coachTrendRows, coachWindowPhrase, coachWindowStart, leakById,
  leakDollarsStand, leakReviewSet, leakSample, methodAccountIds, rankLeaks,
  topLeak, tradesForAccount, tradesForMethod, tradesInCoachWindow, tradesInSpan,
  type CoachWindowId, type LeakSample, type LeakTrade, type RankedLeak,
  type TrendRow,
} from '../../selectors/leaks';
import { orderAccountsForPicker } from '../../selectors/goalForm';

/** Today, by the wall clock, as the report's anchor. */
const todayIso = (now: Date): string =>
  `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

export function CoachReportTab(
  { now = new Date(), go }: { now?: Date; go?: (tab: string) => void },
) {
  const [window, setWindow] = useState<CoachWindowId>(DEFAULT_COACH_WINDOW);
  const [accountId, setAccountId] = useState<string | null>(null);

  const anchor = useMemo(() => todayIso(now), [now]);
  const { accounts } = useAccounts();
  const { prefs } = usePreferences();
  const {
    rules, ledger, canWrite, busy, error: writeError,
    create, bank, retire, mark,
  } = useLeakRules();

  // ⚠ THE READ REACHES BACK TO THE OLDEST RUNNING RULE, whatever the picker
  // says. Prove measures a rule from the day it was written, and a rule older
  // than the selected window would otherwise be re-proved against a truncated
  // population — which reads as though the trader had almost stopped trading.
  const reachBack = useMemo(() => {
    const dates = rules.map((r) => r.createdAt).filter(Boolean).sort();
    return dates[0] ?? null;
  }, [rules]);

  const { phase, trades, error, reload } = useLeakReport(window, anchor, reachBack);

  // ⚠ METHOD ELIGIBILITY FIRST, then the account picker. The old client applies
  // only the first — its loop has no picker at all and is deliberately
  // trader-wide, because a leak is a behaviour that follows you across accounts.
  const eligible = useMemo(
    () => (trades ? tradesForMethod(trades, accounts) : null),
    [trades, accounts],
  );

  // 🚨 THE WINDOW SLICE. The read deliberately reaches back further than the
  // picker asks — to the oldest running rule, and to the prior window for the
  // strip below — and until this line existed every one of those extra trades
  // flowed into Spot it, the four lenses and the AI prompt. A trader with a rule
  // written in July read a "7d" report computed over seven weeks, labelled 7d on
  // every heading. `readSince` widens the READ; this is what keeps the REPORT
  // where the picker put it (app/index.html:41984 does the same).
  const windowStart = useMemo(
    () => coachWindowStart(window, anchor), [window, anchor],
  );
  const windowed = useMemo(
    () => (eligible ? tradesInCoachWindow(eligible, windowStart) : null),
    [eligible, windowStart],
  );

  const scoped = useMemo(
    () => (windowed ? tradesForAccount(windowed, accountId) : null),
    [windowed, accountId],
  );

  // ── the prior window, for the period-over-period strip ─────────────────────
  // ⚠ SCOPED THE SAME WAY AS `scoped` — same account, same method eligibility.
  // A comparison whose two halves came from different populations would report a
  // change the trader never made.
  const priorSpan = useMemo(
    () => coachPriorWindow(window, anchor), [window, anchor],
  );
  const priorScoped = useMemo(
    () => (eligible
      ? tradesForAccount(tradesInSpan(eligible, priorSpan), accountId)
      : null),
    [eligible, priorSpan, accountId],
  );

  // ⚠ THE PICKER OFFERS ONLY WHAT THE METHOD MEASURES. Listing a practice
  // account would let the trader select one and read an empty report with
  // nothing on screen explaining why every leak had gone quiet. Archived
  // accounts DO appear — they count (see `methodAccountIds`) — and sort last,
  // which is the ordering the goal picker already uses.
  const pickable = useMemo(() => {
    const ids = methodAccountIds(accounts);
    return orderAccountsForPicker(
      (accounts ?? []).filter((a) => ids.has(String(a.id))),
    );
  }, [accounts]);

  // 🔴 DO NOT SEED `accountId` FROM `pickable` TO FILL THE VERDICT ON ARRIVAL.
  // It was tried under TCR-15 and reverted, and the reason is not obvious from
  // here: `accountId` does not scope only the verdict — `scoped` (above) feeds
  // the WHOLE Spot-it ledger, the four lenses and the AI prompt. Defaulting it
  // to one account silently narrows the leak analysis from trader-wide to
  // single-account, and the report's headline numbers change for everyone.
  //
  // Measured: it breaks PD-11's own test — an archived account's trade stops
  // counting, `2 of 2 trades` becomes `1 of 1 trade` — which is the parity claim
  // that this loop follows the TRADER, not the account (see the comment above
  // `eligible`, and beta's loop, which has no picker at all).
  //
  // ▶ Beta fills those panels because its report is single-account THROUGHOUT.
  // This client deliberately made the loop trader-wide and added a picker on
  // top, so one control cannot serve both. Filling the verdict without narrowing
  // the ledger needs the verdict and the ledger to read different scopes — a
  // design decision, not a default.

  // ⚠ THE CATALOGUE IS A FALLBACK, NOT THE SOURCE. Only premature-exit reads it,
  // and only for the one trade shape whose stored run cannot price itself — one
  // stopped on its first bar, whose run is exactly zero points and zero dollars.
  // A null read (still loading, or failed) passes an empty list, and those trades
  // are then counted as unpriced rather than as costing nothing.
  const { contracts } = useContracts();
  const ctx = useMemo(
    () => ({
      controllableTags: prefs?.controllableMistakeTags ?? {},
      contracts: (contracts ?? []) as never,
    }),
    [prefs?.controllableMistakeTags, contracts],
  );

  const ranking = useMemo(
    () => (scoped ? rankLeaks(scoped as unknown as LeakTrade[], ctx) : null),
    [scoped, ctx],
  );
  const start = useMemo(
    () => (scoped ? topLeak(scoped as unknown as LeakTrade[], ctx) : null),
    [scoped, ctx],
  );
  const upside = useMemo(
    () => (scoped ? topLeak(scoped as unknown as LeakTrade[], ctx, 'opportunity') : null),
    [scoped, ctx],
  );
  const sample = useMemo(
    () => (scoped ? leakSample(scoped as unknown as LeakTrade[]) : null),
    [scoped],
  );

  // ⚠ TWO POPULATIONS ON ONE SCREEN, deliberately (D77/D80). Spot it slices to
  // the picker and the window because looking at one account over one month is
  // analysis. A RULE is a habit, so Fix it and Prove it read every method-
  // eligible trade — the account picker does not reach them, and neither does
  // the window. Freezing a baseline over one population and re-proving it over
  // another would report a difference nobody made.
  const traderWide = eligible;

  // ── the AI verdict ─────────────────────────────────────────────────────────
  // ⚠ ONE ACCOUNT ONLY. The coach reasons about a drawdown limit and a remaining
  // cushion, and those are different numbers on every account — see
  // `domain/aiVerdict.ts`. The gate is checked BEFORE any request, because each
  // one costs money and burns a capped daily allowance.
  const verdictAccount = useMemo<VerdictAccount | null>(() => {
    if (!accountId) return null;
    const a = (accounts ?? []).find((x) => String(x.id) === accountId);
    return a ? (a as unknown as VerdictAccount) : null;
  }, [accounts, accountId]);

  // 🚨 THE ONE PRO ACTION ON THIS TAB THAT NOBODY CLICKS (#76). The verdict card
  // fires a billed model call from an effect as soon as the key is non-null, so
  // the gate below is what stops it — there is no button to guard.
  const aiGate = useProAction('ai-coach');
  const gate = useMemo(
    () => verdictGate(verdictAccount, scoped?.length ?? 0, aiGate.blocked == null),
    [verdictAccount, scoped, aiGate.blocked],
  );

  const aiPayload = useMemo(
    () => (gate.ok && verdictAccount && scoped
      ? buildVerdictPayload(
        verdictAccount, coachWindowPhrase(window),
        scoped as unknown as LeakTrade[], prefs?.controllableMistakeTags,
      )
      : null),
    [gate.ok, verdictAccount, scoped, window, prefs?.controllableMistakeTags],
  );

  // ⚠ THE KEY CARRIES THE WINDOW AS WELL AS THE ACCOUNT. The old client keys on
  // account|period only, which is the same thing there — here the window control
  // is on this tab, and omitting it would serve a 7-day verdict on a 90-day view.
  const aiKey = gate.ok && verdictAccount
    ? `${verdictAccount.id}|${window}|${anchor}` : null;
  const ai = useAiVerdict({ key: aiKey, payload: aiPayload });

  // ── the four dimensions ────────────────────────────────────────────────────
  const { patterns } = usePatterns();
  const { exitModels } = useExitModels();

  /**
   * ⚠ ONE READER, BUILT ONCE PER REGISTRY. It resolves a trade's stored
   * confluences to the marks that may be ranked — both stored shapes, category
   * parents dropped, orphans kept and flagged — and it is the identical
   * function the Confluence Edge card and the Results board read through.
   */
  const confMarks = useMemo(
    () => confluenceMarkReader(prefs?.confluences), [prefs?.confluences],
  );

  const nameLookups = useMemo(() => {
    const conf = new Map<string, string>();
    // ⚠ FLATTENED, parents AND children. A trade tags a leaf, and a ranking that
    // only knew the categories would render every row as a raw id.
    for (const node of confluenceRegistry(prefs?.confluences)) {
      conf.set(node.id, node.label);
      for (const c of node.children ?? []) conf.set(c.id, c.label);
    }
    const pat = new Map<string, string>();
    for (const p of patterns ?? []) pat.set(String(p.id), String(p.name ?? ''));
    const ex = new Map<string, string>();
    for (const e of exitModels ?? []) ex.set(String(e.id), String(e.name ?? ''));
    return { conf, pat, ex };
  }, [prefs?.confluences, patterns, exitModels]);

  /**
   * Risk of ruin over one population of trades.
   *
   * ⚠ RUIN IS AN ACCOUNT'S NUMBER. A trailing drawdown belongs to one account,
   * and resampling a merged pool would model a trader whose losses on one
   * combine eat another's cushion. Same reasoning as the AI verdict.
   *
   * ⚠ ONE FUNCTION, TWO WINDOWS. The strip compares this period's ruin against
   * the prior one, and a second copy of the model — a resampling run with its
   * own seed, its own starting balance and its own reading of the firm's rules —
   * is exactly the drift the rebuild exists to end. The only thing that differs
   * between the two calls is the trades and the seed.
   *
   * ⚠ THE STARTING BALANCE IS TODAY'S IN BOTH, which is the old client's
   * behaviour and is worth stating rather than discovering: the prior period's
   * ruin is "what that period's daily results would do to the account you have
   * NOW", not a reconstruction of the account as it stood then. It answers "has
   * my trading got safer", which is the question the strip asks.
   */
  const ruinOf = useCallback(
    (ts: readonly unknown[], seed: string) => {
      const rules = verdictAccount?.rules as Record<string, unknown> | undefined;
      if (!verdictAccount || !rules) return null;
      return riskOfRuin({
        days: dailyPnl(ts as never),
        start: Number(verdictAccount.currentBalance)
          || Number(verdictAccount.accountSize) || 0,
        rules: {
          maxDrawdown: Number(rules['maxDDValue']) || 0,
          profitTarget: Number(rules['profitTarget']) || null,
          lockMechanic: rules['lockMechanic'] as string | null,
          lockPad: Number(rules['lockPad']) || 0,
        },
        // ⚠ SEEDED on the account, window and day — so the same book always
        // shows the same figure. A risk number that moves on every render is
        // not a risk number.
        rand: seededRandom(seed),
      });
    },
    [verdictAccount],
  );

  const dims = useMemo<DimensionData | null>(() => {
    if (!scoped) return null;
    const ts = scoped as unknown as LeakTrade[];
    const cap = mfeCapture(ts);
    const days = dailyPnl(scoped);
    const ruin = ruinOf(scoped, `${verdictAccount?.id ?? ''}|${window}|${anchor}`);

    return {
      ruin,
      ruinDays: days.length,
      ruinAccount: verdictAccount?.name ?? null,
      ruinBlocked: verdictAccount ? null
        : 'Pick one account above. A trailing drawdown belongs to a single '
          + 'account, so resampling several together would model a trader whose '
          + 'losses on one combine eat another’s cushion.',
      edge: edgeQuality(scoped),
      capturePct: cap.pct,
      captureN: cap.n,
      // 🚨 THE SHARED READER, not a name lookup. This passed
      // `(id) => nameLookups.conf.get(id)` and let the engine walk the stored
      // map itself — which compared `{ active: true }` against `true` and so
      // ranked NOTHING, on every trade this client has ever saved.
      //
      // ⚠ The name comes back on the mark, so `nameLookups.conf` is no longer
      // read here. It still builds, because the AI verdict payload below uses
      // it — removing it would be a second change wearing this one's clothes.
      confluences: confluenceRanking(scoped, (t) => confMarks(t.confluences)),
      pairs: pairings(
        scoped,
        (id) => nameLookups.pat.get(id) ?? null,
        (id) => nameLookups.ex.get(id) ?? null,
      ),
    };
  }, [scoped, verdictAccount, nameLookups, window, anchor, ruinOf]);

  /**
   * The period-over-period strip (Pro) — `app/index.html:42140`.
   *
   * ⚠ COMPUTED WHATEVER THE ENTITLEMENT SAYS, and hidden at render. The read
   * that feeds it already happened (`useLeakReport` widens to cover the prior
   * window), so gating the computation would save nothing and would make the
   * strip's presence depend on the order two async reads happened to settle in.
   */
  const trend = useMemo<TrendRow[] | null>(() => {
    if (!scoped || !priorScoped || !priorSpan) return null;
    return coachTrendRows({
      current: coachHeadline(scoped),
      prior: coachHeadline(priorScoped),
      ruinPct: dims?.ruin?.blowPct ?? null,
      priorRuinPct: ruinOf(
        priorScoped, `${verdictAccount?.id ?? ''}|${window}|${anchor}|prior`,
      )?.blowPct ?? null,
    });
  }, [scoped, priorScoped, priorSpan, dims, ruinOf, verdictAccount, window, anchor]);

  /**
   * 🚨 ONE CHOKE POINT FOR THE WHOLE FIX ACT (#76).
   *
   * Three controls reach this — the "Fix it →" on a leak row, the button on the
   * Fix-it card, and Prove it's "Sharpen the rule" — and the old client guards
   * the same way, with one `requirePro('Fix it')` on the modal open
   * (app/index.html:43466) rather than three guards on three buttons.
   *
   * ⚠ THIS TAB IS FREE AND THAT IS DELIBERATE. "Spot it" — what your habits are
   * costing you — is the free half of the methodology the product is sold on;
   * Fix it and Prove it are the paid half. A tab gate cannot express that, which
   * is the whole reason the action gate exists.
   */
  // ⚠ IT ALSO FEEDS `canFix` ON THE SPOT CARD, not just this handler. The old
  // client passes `actionable: pro` into the same card (app/index.html:43365),
  // so a free user’s leak rows carry no "Fix it →" at all — the refusal dialog
  // is the second line of defence, not the first. A row that offers an action
  // it will refuse is a worse screen than one that does not offer it.
  const fixGate = useProAction('fix-it');

  /**
   * 🚨 THE SECTION LOCK — the half of the paywall no click guard can reach.
   *
   * Gating buttons leaves everything with no button in front of it on screen:
   * the running rules, each rule's verdict, and the banked-habits ledger. The
   * paid half of this method is mostly READING. So the whole Fix + Prove block
   * is swapped for one lock card, exactly as the old client does it
   * (app/index.html:43372), and the four lenses likewise (:42266).
   *
   * ⚠ `pending` IS READ IN THE OPPOSITE DIRECTION FROM `useProAction`, and the
   * difference is deliberate — see `proSectionLocked`, which is where that
   * asymmetry is written down and tested. An action has no frame to leak, so it
   * ALLOWS while the read is in flight rather than refuse a paying customer. A
   * SECTION is drawn immediately, and access fails open on an unknown read, so
   * drawing it during the read would flash the paid content at a free user on
   * every visit. This is the tab gate's rule (`App.tsx`), applied to part of a
   * tab.
   *
   * ⚠ `proLocked === false` IS NOT PERMISSION TO DRAW — it is also false while
   * the read is in flight. Both branches below check `proPending` first.
   *
   * ⚠ A SECOND ENTITLEMENT READ. `useEntitlement` holds no shared state, so each
   * caller reads the table itself — this tab now makes three. They cannot
   * disagree in the dangerous direction: a failed read grants access, so the
   * worst case is a locked section beside an allowed action, which the refusal
   * dialog then explains. Reading it once per app is a real fix and a separate
   * one.
   */
  const { pro, pending: proPending } = useEntitlement();
  const proLocked = proSectionLocked({ pro, pending: proPending });
  const seePlans = go ? () => go('account') : undefined;

  const [fixRefused, setFixRefused] = useState(false);
  const [fixing, setFixing] = useState<{ leakId: string; prefill: string } | null>(null);
  const openFix = useCallback(
    (leakId: string, prefill: string) => {
      if (fixGate.blocked) { setFixRefused(true); return; }
      setFixing({ leakId, prefill });
    },
    [fixGate.blocked],
  );

  // ── the review drill ───────────────────────────────────────────────────────
  // ⚠ DRAWN FROM `scoped`, THE SAME LIST SPOT IT MEASURED — not from `traderWide`
  // and not from the raw read. The row above says "12 of 85 trades" about the
  // account and window on screen; opening a list built from any other population
  // would show a trader trades that are not the ones the number came from, with
  // nothing to explain the difference.
  const [reviewing, setReviewing] = useState<string | null>(null);
  const review = useMemo(() => {
    if (!reviewing || !scoped) return null;
    const set = leakReviewSet(reviewing, scoped as unknown as LeakTrade[], ctx);
    if (!set) return null;
    return { label: leakById(reviewing)?.label ?? reviewing, ...set };
  }, [reviewing, scoped, ctx]);

  // The next leak worth a rule: the biggest realized one without a rule, else
  // the biggest upside one. Measured on what is ON SCREEN, because that is the
  // list the trader is reading when they press the button.
  const ruled = useMemo(() => new Set(rules.map((r) => r.leakId)), [rules]);
  const nextLeakId = useMemo(() => {
    if (!ranking) return null;
    const pick = (rows: RankedLeak[]) =>
      rows.find((r) => leakDollarsStand(r) && !ruled.has(r.id));
    return (pick(ranking.realized) ?? pick(ranking.opportunity))?.id ?? null;
  }, [ranking, ruled]);

  return (
    <div className="coach-tab" data-testid="coach-report">
      <header className="coach-head">
        {/* ⚠ THE MOTTO IS THE METHOD'S NAME, and it was missing entirely from
            the first pass. Beta leads the tab with it — "Spot it. Fix it. Prove
            it." is the product's whole argument, and a reader who never sees it
            has no idea the section below is step one of three. */}
        <h2 className="coach-motto">
          Spot it. Fix it. Prove it.<EduMark eduKey="coach.method" />
        </h2>
        <p className="muted coach-tag">
          Find the habits costing you the most, write a rule for each, and prove
          each one moved its number.
        </p>
        <div className="row gap-8 coach-controls">
          <label className="coach-acct">
            <span className="muted">Account</span>
            <select
              value={accountId ?? ''}
              onChange={(e) => setAccountId(e.target.value === '' ? null : e.target.value)}
            >
              <option value="">All accounts</option>
              {pickable.map((a) => (
                <option key={String(a.id)} value={String(a.id)}>{a.name}</option>
              ))}
            </select>
          </label>
          <span className="grow" />
          {/* ⚠ THIS TAB'S OWN WINDOW — it does not ride the global filter. See
              `domain/coachWindow.ts` for why that is load-bearing. */}
          <div className="coach-windows" role="group" aria-label="Report window">
            {COACH_WINDOWS.map((w) => (
              <button
                key={w.id}
                type="button"
                className={`btn coach-win${window === w.id ? ' on' : ''}`}
                aria-pressed={window === w.id}
                onClick={() => setWindow(w.id)}
              >{w.label}</button>
            ))}
          </div>
        </div>
        {/* ⚠ PRO, AND HIDDEN RATHER THAN LOCKED. Beta shows a free user nothing
            here — no card, no teaser — and sells the comparison in the four-
            dimensions lock instead. A second lock card in the header would put
            two pitches on one screen for one purchase.
            ⚠ `proPending` FIRST: `proLocked` is also false while the
            subscription read is in flight, and drawing then would flash the paid
            strip at a free user on every single visit. */}
        {!proPending && !proLocked && trend ? (
          <CoachTrendStrip rows={trend} window={window} />
        ) : null}
      </header>

      {phase === 'loading' ? (
        <p className="muted">Reading your trades…</p>
      ) : phase === 'failed' || !ranking || !scoped ? (
        // ⚠ NOT an empty report. "No leaks this period" over a failed read is the
        // most misleading sentence this screen could produce.
        <div className="card" role="alert">
          <h3>Couldn’t read your trades</h3>
          <p className="muted">
            Nothing has been measured, so nothing below would be true. {error}
          </p>
          <button type="button" className="btn primary" onClick={reload}>Try again</button>
        </div>
      ) : (
        <>
          {fixRefused ? (
            <ProActionLimit
              title={PRO_ACTION_LABEL['fix-it']}
              message={fixGate.blocked ?? ''}
              pitch={PRO_ACTION_PITCH['fix-it']}
              onClose={() => setFixRefused(false)}
              onSeePlans={seePlans}
            />
          ) : null}

          <CoachVerdict
            gate={gate}
            onSeePlans={seePlans}
            state={ai}
            accountName={verdictAccount?.name ?? null}
            tradeCount={scoped.length}
            netPnl={sample?.net ?? 0}
          />

          <SpotIt
            ranking={ranking}
            start={start}
            upside={upside}
            window={window}
            sample={sample ?? leakSample([])}
            ruled={ruled}
            canFix={canWrite && fixGate.blocked == null}
            onFix={openFix}
            onReview={setReviewing}
          />

          {proPending ? <CoachSectionChecking /> : proLocked ? (
            <CoachProLock onSeePlans={seePlans} />
          ) : (
            <>
              {/* ⚠ A FAILED SETTINGS READ BLOCKS EVERY WRITE, and it is said once
                  here rather than as a disabled button on each of eleven controls.
                  See `useLeakRules` — a save then would overwrite settings this
                  screen never read.

                  ⚠ AND ONLY WHERE THERE IS SOMETHING TO WRITE. Under the lock a
                  free user cannot start a rule at all, so a warning that rules
                  are temporarily read-only would name a restriction that is not
                  the one they are actually under. */}
              {!canWrite ? (
                <div className="card" role="alert">
                  <h3>Rules are read-only right now</h3>
                  <p className="muted">
                    Your settings haven’t loaded, and rules live alongside them —
                    saving one now would overwrite the settings this screen never
                    read. Everything above is still accurate. Reload to write again.
                  </p>
                </div>
              ) : null}

              <FixIt rules={rules} nextLeakId={nextLeakId} onFix={openFix} />

              <ProveIt
                rules={rules}
                trades={traderWide as never}
                ctx={ctx}
                today={anchor}
                busy={busy}
                onMark={(tradeId, ruleId, followed) => {
                  void mark(tradeId, ruleId, followed).then((e) => { if (!e) reload(); });
                }}
                onBank={(id) => { void bank(id, anchor); }}
                onRetire={(id) => { void retire(id, 'retired without proof', anchor); }}
                onSharpen={openFix}
              />
            </>
          )}

          {/* ⚠ THE LOCK RENDERS EVEN WHEN THERE IS NO DATA BEHIND IT, which the
              real panel does not. A free user with a thin book still needs to
              know the section exists — a lens they cannot see is one they cannot
              want. The old client renders its dimensions lock unconditionally
              for the same reason. */}
          {proPending ? null : proLocked ? (
            <CoachDimensionsLock onSeePlans={seePlans} />
          ) : dims ? <CoachDimensions d={dims} window={window} /> : null}

          {/* The ledger is the tail of the Prove act, so it locks with it. */}
          {proPending || proLocked ? null : <CoachLedger rows={ledger} />}

          {/* ⚠ THE OLD CLIENT'S FOOTER, and it is the only line on the screen
              that states the shape of the deal. Shown to everyone, as it is
              there — a paying reader learns what their money bought, and a free
              one learns the locks above are not an error. */}
          <p className="muted" style={{ fontSize: 12, lineHeight: 1.6, marginTop: 4 }}>
            <b>Free</b> shows what’s costing you — Spot it. <b>Pro</b> unlocks Fix
            it, Prove it, the AI Coach verdict, and the full account analysis.
          </p>

          {writeError ? (
            <div className="card" role="alert">
              <h3>That didn’t save</h3>
              <p className="muted">{writeError}</p>
            </div>
          ) : null}
        </>
      )}

      {review ? (
        <CoachDrill
          label={review.label}
          trades={review.trades as unknown as DrillTrade[]}
          column={review.column}
          onClose={() => setReviewing(null)}
          onChanged={reload}
        />
      ) : null}

      {fixing ? (
        <FixDialog
          /**
           * 🔴 KEYED ON THE LEAK, and it is not decoration (TCR-86). The draft
           * slot is `coachfix:draft:<leakId>`, and `useTabMemory` reads its slot
           * through a `useState` initialiser — which runs ONCE. So swapping
           * `leakId` on a mounted dialog leaves the previous leak's rule in the
           * box under the new leak's heading, with Save enabled.
           *
           * ⚠ Today every route between two leaks closes the dialog first, so
           * this cannot be reached by clicking — which is exactly why it would
           * have shipped. `TradeDetail` records the same trap and the same fix
           * for stepping ‹ › between trades (D65).
           */
          key={fixing.leakId}
          leakId={fixing.leakId}
          prefill={fixing.prefill}
          trades={(traderWide ?? []) as unknown as LeakTrade[]}
          ctx={ctx}
          busy={busy}
          saveError={writeError}
          onCancel={() => setFixing(null)}
          /**
           * ⚠ REPORTS THE OUTCOME BACK (TCR-86). It already closed only on
           * success, so a refused write kept the dialog open with the typing in
           * it. Now the dialog also needs to know, because it holds a draft
           * above the tab and must throw it away on a save and keep it on a
           * refusal — and those are the same click from in there.
           */
          onSave={async (baseline, text) => {
            const e = await create({
              leakId: fixing.leakId, ruleText: text, baseline, today: anchor,
            });
            if (e) return false;
            setFixing(null);
            return true;
          }}
        />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// The period-over-period strip (Pro)
// ---------------------------------------------------------------------------

/** `67%`, `+0.4R`, `+$1,204`. */
function trendValue(r: TrendRow): string {
  if (r.unit === 'pct') return `${num(r.value, 0)}%`;
  if (r.unit === 'r') return `${r.value >= 0 ? '+' : '−'}${num(Math.abs(r.value), 1)}R`;
  return money(r.value, 0);
}

/**
 * The change, SIGNED.
 *
 * ⚠ A DELIBERATE DEVIATION FROM BETA, which prints the magnitude alone and
 * leaves the arrow to carry the direction. The arrow is `aria-hidden` here —
 * this client's own convention, set by the analytics hero pill — so a reader
 * using a screen reader would hear "Net $300" on the period the account lost
 * three hundred dollars. The sign says it in text; the arrow repeats it in
 * glyph form for everyone else.
 */
function trendDelta(r: TrendRow): string {
  const d = r.delta ?? 0;
  if (r.unit === 'money') return money(d, 0);
  const sign = d > 0 ? '+' : '−';
  return sign + num(Math.abs(d), r.unit === 'r' ? 1 : 0);
}

function CoachTrendStrip(
  { rows, window }: { rows: readonly TrendRow[]; window: CoachWindowId },
) {
  return (
    <div className="coach-trend" data-testid="coach-trend">
      {/* ⚠ "the last 30 days" → "30 days". Stripping only "the " leaves "vs
          prior last 30 days", which is what the first run of the test read back.
          The phrase is shared with the headings above, so it is trimmed here
          rather than adding a second wording of the same window. */}
      <span className="muted">
        vs prior {coachWindowPhrase(window).replace(/^the last /, '')}:
      </span>
      {rows.map((r) => (
        <span key={r.id} className="coach-trend-seg">
          {r.label}{' '}
          <b className={r.good === null ? '' : r.good ? 'up' : 'down'}>
            {trendValue(r)}
            {r.delta === null ? null : (
              <>
                {' '}
                {/* ⚠ THE ARROW FOLLOWS THE NUMBER, NOT THE VERDICT. A falling
                    risk of ruin is good AND is a ▼ — the colour says whether it
                    helped, the arrow says which way it moved. Making the arrow
                    agree with the colour would tell a trader their ruin went UP
                    in the period it halved. */}
                <span aria-hidden="true">{r.delta > 0 ? '▲' : '▼'}</span>
                {trendDelta(r)}
              </>
            )}
          </b>
        </span>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Spot it
// ---------------------------------------------------------------------------

function SpotIt(
  { ranking, start, upside, window, sample, ruled, canFix, onFix, onReview }: {
    ranking: { realized: RankedLeak[]; opportunity: RankedLeak[] };
    start: RankedLeak | null;
    upside: RankedLeak | null;
    window: CoachWindowId;
    sample: LeakSample;
    /** Leak ids that already have a running rule. */
    ruled: ReadonlySet<string>;
    canFix: boolean;
    onFix: (leakId: string, prefill: string) => void;
    onReview: (leakId: string) => void;
  },
) {
  return (
    <section className="card" data-testid="spot-it">
      <div className="coach-step-head">
        <span className="coach-step-n" aria-hidden="true">1</span>
        <div>
          {/* ⚠ `.coach-step-t`, NOT an `<h3>` — the shared card rule uppercases
              every h3, and beta writes "Spot it" in sentence case. The first
              pass rendered "SPOT IT", which reads as a section label rather than
              as step one of a method. */}
          <div className="coach-step-t">Spot it<EduMark eduKey="coach.spot" /></div>
          <p className="muted">What’s costing you, over {coachWindowPhrase(window)}.</p>
        </div>
      </div>

      {sample.n === 0 ? (
        <p className="muted coach-empty">
          No trades in this window. Widen it, or pick a different account.
        </p>
      ) : (
        <>
          {/* ⚠ RED. Beta colours this line, and it is the one sentence the whole
              tab exists to deliver — in body colour it reads as another
              paragraph. */}
          {start ? (
            <p className="coach-start red">
              <b>Start here: {start.label}</b> — {money(-start.dollars)} across{' '}
              {num(start.n, 0)} trade{start.n === 1 ? '' : 's'}. {start.help}{' '}
              <span className="muted">
                This is money already booked, so a rule can win it back.
              </span>
            </p>
          ) : ranking.realized.some(leakStandsBehindClean) ? (
            // ⚠ "Start here: $0" is not a starting point.
            <p className="coach-start muted">
              Nothing in the realized column cost you anything over{' '}
              {coachWindowPhrase(window)}. That is the finding, not an error.
            </p>
          ) : (
            // 🚨 A FOURTH NOTHING, and it is the opposite claim. When not one
            // realized leak could be measured, "nothing cost you anything" is
            // the app telling the trader their period was clean on the strength
            // of having looked at nothing. The rows below already draw this
            // distinction line by line; the headline was the one place that did
            // not, and it was found the same way — by reading the screen.
            //
            // ⚠ AND IT STATES THE FACTS IT DOES HAVE. Seven em dashes over an
            // account that lost $3,185 in two minutes is honest and useless — it
            // reads as though the app had lost the data. Count, outcome and net
            // need no measurement and name no habit, which is the line this
            // whole section is drawn along.
            <p className="coach-start muted" data-testid="coach-unmeasured">
              <b>{outcomeLine(sample)} over {coachWindowPhrase(window)}.</b>{' '}
              Too few to measure any habit yet — nothing below has been looked
              at, which is not the same as a clean period.
            </p>
          )}

          <Ledger
            title="Money you lost"
            blurb="Dollars already booked against you. Realized, and fixable with a rule."
            rows={ranking.realized}
            signed
            ruled={ruled}
            canFix={canFix}
            onFix={onFix}
            onReview={onReview}
          />

          <Ledger
            title="Money you left"
            blurb={'A counterfactual — what a different decision MIGHT have been '
              + 'worth. An upper bound, never recoverable P&L, and never ranked '
              + 'against the column above. It is not the same money.'}
            rows={ranking.opportunity}
            ruled={ruled}
            canFix={canFix}
            onFix={onFix}
            onReview={onReview}
          />

          {upside ? (
            <p className="muted coach-upside">
              {/* ⚠ `moneyPlain`, for the same reason as the ledger rows above:
                  this is the largest counterfactual, not a gain. */}
              <b>Biggest upside: {upside.label}</b> — up to {moneyPlain(upside.dollars)}.{' '}
              Only a <b>conditional</b> rule reaches this — trail the stop, or hold
              only under a specific setup. A blanket “hold longer” pays for the
              runners with the stop-outs.
            </p>
          ) : null}
        </>
      )}

    </section>
  );
}

function Ledger(
  { title, blurb, rows, signed, ruled, canFix, onFix, onReview }: {
    title: string; blurb: string; rows: RankedLeak[]; signed?: boolean | undefined;
    ruled: ReadonlySet<string>;
    canFix: boolean;
    onFix: (leakId: string, prefill: string) => void;
    onReview: (leakId: string) => void;
  },
) {
  return (
    <div className="coach-ledger">
      <h4 className="coach-ledger-h">{title}</h4>
      <p className="muted coach-ledger-blurb">{blurb}</p>
      <div className="coach-rows">
        {rows.map((r) => (
          <LeakRow key={r.id} row={r} signed={signed}
            hasRule={ruled.has(r.id)} canFix={canFix} onFix={onFix}
            onReview={onReview} />
        ))}
      </div>
    </div>
  );
}

/**
 * Can this row support the sentence "no leak here this period"?
 *
 * ⚠ `!thin` IS THE HALF THAT MATTERS. A rate measured over three trades against
 * a minimum of twenty is not evidence of no leak — it is not evidence either
 * way, which is exactly the ordering `note()` uses one row down. The headline
 * was the one place that skipped it and declared a clean period off a sample
 * every row beneath it called thin.
 */
const leakStandsBehindClean = (r: RankedLeak): boolean =>
  r.available && r.rate.value !== null && !r.thin;

/**
 * "3 trades, all losses, −$3,185.00" — the facts, and only the facts.
 *
 * ⚠ A SCRATCH IS COUNTED SEPARATELY and never folded into either side. Beta's
 * own convention (`netPnl >= 0` is not a loss, `> 0` is a win) leaves flat
 * trades in their own category, and calling one a win here would be a small lie
 * on the one line of this screen that is supposed to be beyond argument.
 */
function outcomeLine(s: LeakSample): string {
  const trades = `${num(s.n, 0)} trade${s.n === 1 ? '' : 's'}`;
  // ⚠ `moneyPlain`, NOT `money`. `money` supplies its own sign, so prefixing an
  // em dash to it produces "−+$3,185.00" — which is PD-3, the double-sign bug
  // already recorded against the old client. Caught by a test rather than by a
  // screenshot, for once.
  const net = `${s.net < 0 ? '−' : ''}${moneyPlain(Math.abs(s.net))}`;
  const shape = s.losses === s.n ? 'all losses'
    : s.wins === s.n ? 'all winners'
    : s.scratches === s.n ? 'all scratches'
    : [
      s.wins ? `${num(s.wins, 0)} winner${s.wins === 1 ? '' : 's'}` : '',
      s.losses ? `${num(s.losses, 0)} loss${s.losses === 1 ? '' : 'es'}` : '',
      s.scratches ? `${num(s.scratches, 0)} scratch${s.scratches === 1 ? '' : 'es'}` : '',
    ].filter(Boolean).join(', ');
  return `${trades}, ${shape}, ${net}`;
}

function LeakRow(
  { row, signed, hasRule, canFix, onFix, onReview }: {
    row: RankedLeak; signed?: boolean | undefined;
    hasRule: boolean; canFix: boolean;
    onFix: (leakId: string, prefill: string) => void;
    onReview: (leakId: string) => void;
  },
) {
  // ⚠ THE AMOUNT AND THE SENTENCE NOW ANSWER TO THE SAME TEST. They used to be
  // gated on different measurements — the money on the dollar total, the words
  // on the rate — so one row could print "−$2,602.50" beside "Not enough data to
  // measure this period". Found on Josh's side-by-side, not by any test.
  const hasDollars = leakDollarsStand(row);
  const n = note(row);
  // ⚠ THE COUNT IS A CONTROL ONLY WHEN THERE IS SOMETHING BEHIND IT. `row.n` is
  // the contributor count — the same number the leak's own `contributors` list
  // returns — so a zero here means the review list would open empty. A link into
  // nothing reads as the app being broken rather than as a clean period.
  const reviewable = n.count && n.n > 0;

  return (
    <div className="coach-row" data-leak={row.id}>
      <div className="grow">
        <div className="coach-row-label">
          {row.label}<EduMark eduKey={row.eduKey} />
        </div>
        <div className="muted coach-row-note">
          {!n.count ? n.text : (
            <>
              {reviewable ? (
                <button type="button" className="coach-review"
                  onClick={() => onReview(row.id)}
                  title={`Review these ${num(n.n, 0)} trade${n.n === 1 ? '' : 's'} — see what happened`}
                >
                  {num(n.n, 0)} of {num(n.population, 0)} trade
                  {n.population === 1 ? '' : 's'} · review →
                </button>
              ) : (
                <>{num(n.n, 0)} of {num(n.population, 0)} trade{n.population === 1 ? '' : 's'}</>
              )}
              {n.tail}
            </>
          )}
        </div>
      </div>
      {/* 🔴 THE TWO LEDGERS FORMAT DIFFERENTLY, AND THAT IS THE POINT.
          "Money you lost" is booked and gone, so it reads as a loss: negative
          and red. "Money you left" is a counterfactual — an upper bound on what
          a different decision MIGHT have been worth — so it reads plain and
          gold, and NEVER carries a plus.

          ⚠ It used to. `money()` prefixes a '+' to any positive number, and the
          opportunity ledger passes no `signed`, so every figure in it rendered
          "+$27,990.00" — money forgone, printed exactly like money made. Beta
          says so in as many words at app/index.html:43059: "A leak dollar figure
          is never a gain — so never fmtMoney's '+'", with the colour gold rather
          than red (leakAmountColor, :43067).

          ▶ A fix that makes these two branches the same is wrong. */}
      <div className={`mono coach-row-val${hasDollars ? (signed ? ' red' : ' gold') : ''}`}>
        {/* ⚠ An EM DASH, never $0. Zero is a measurement — "you lost nothing to
            this" — and a row that could not be measured must not borrow it. */}
        {hasDollars
          ? (signed ? money(-row.dollars) : moneyPlain(row.dollars))
          : '—'}
      </div>
      {/* ⚠ NO BUTTON WHERE THERE IS NOTHING TO FREEZE. A leak with no measured
          number cannot produce a usable baseline, so the action would open a
          form that refuses on arrival — which reads as the app being broken
          rather than the sample being thin. */}
      {hasRule ? (
        <span className="coach-ruleon" title="A rule for this leak is running — see Prove it below">
          ● Rule active
        </span>
      ) : canFix && hasDollars ? (
        <button type="button" className="btn coach-rowfix"
          onClick={() => onFix(row.id, '')}>Fix it →</button>
      ) : null}
    </div>
  );
}

/**
 * The sentence under a leak's name.
 *
 * ⚠ THE ORDER OF THESE BRANCHES IS THE HONESTY. Unavailable is checked before
 * thin, and thin before clean — because a leak that cannot be measured is also
 * technically "not enough data", and telling the trader to keep trading would
 * offer a remedy that does not apply.
 *
 * ⚠ RETURNS PARTS, NOT A STRING. The contributor count is a control — it opens
 * the exact trades behind the number — so the line has to be assembled from a
 * piece the row can hang a button on and a piece it cannot. Two of these six
 * branches carry a count; the other four are prose, and offering a review link
 * on "not enough data to measure this period" would drill into nothing.
 */
type LeakNote =
  | { count: false; text: string }
  | { count: true; n: number; population: number; tail: string };

const plain = (text: string): LeakNote => ({ count: false, text });

function note(r: RankedLeak): LeakNote {
  if (!r.available) {
    return plain(`Not measured in this client yet — needs ${r.needs ?? 'data it cannot read'}.`);
  }
  if (r.rate.value === null) {
    return plain(r.needs
      ? `Not enough data to measure this period — needs ${r.needs}.`
      : 'Not enough data to measure this period.');
  }
  // ⚠ THIN IS CHECKED BEFORE CLEAN, AND THAT ORDER IS THE HONEST ONE — a
  // DELIBERATE divergence from the old client, which says "No leak here this
  // period." whatever the sample size. Two clean trades against a minimum of ten
  // is not evidence of no leak; it is not enough evidence either way, and the
  // stronger sentence would be the app overclaiming on the trader's behalf.
  if (r.thin) {
    return {
      count: true, n: r.n, population: r.population,
      tail: `${caveat(r)} · thin sample, need ${r.minSample}.`,
    };
  }
  // ⚠ MEASURED, BUT WITH NO DOLLAR FACE. Capture efficiency reports a real
  // percentage over a real sample and has no total here. Before this branch
  // existed it fell through to the clean case and rendered "No leak here this
  // period — measured over 0 trades", which contradicts itself in one sentence.
  // Found by opening the tab on Josh's book; no test had asked.
  if (!r.dollarsAvailable) {
    return plain(`${rateText(r)} over ${num(r.rate.n, 0)} trades · `
      + 'the dollar value of this one is not computed here yet.');
  }
  if (r.dollars <= 0) {
    // Measured, enough of it, and clean. A finding in its own right.
    return plain(`No leak here this period — measured over ${num(r.rate.n, 0)} trades.`);
  }
  return { count: true, n: r.n, population: r.population, tail: `${caveat(r)}.` };
}

/**
 * The facts that must never be separated from the dollar figure.
 *
 * ── 🚨 THE MOST DANGEROUS NUMBER IN THE REPORT, TOLD HONESTLY ──
 *
 * `Cost of premature exit` reads **+$2,872.50** on Josh's own book — and on the
 * same book roughly two thirds of those exits were PROTECTIVE: holding would have
 * run straight into the stop. A trader who sees only the total writes "hold
 * longer", which pays for the runners with the stop-outs.
 *
 * ⚠ THIS WAS MISSING FROM THE FIRST PORT and only turned up by reading the
 * screen. The engine had computed `stoppedPct` all along; nothing carried it to
 * the row. A number whose caveat is computed and then dropped is worse than one
 * that was never measured — it looks complete.
 */
function caveat(r: RankedLeak): string {
  const bits: string[] = [];
  if (r.stoppedPct !== null) {
    bits.push(`${num(r.stoppedPct, 0)}% of those exits were protective — `
      + 'holding would have been stopped out');
  }
  if (r.upperBound) bits.push('an upper bound, not recoverable P&L');
  return bits.length ? ` · ${bits.join(' · ')}` : '';
}

/**
 * A leak's rate, in its own unit.
 *
 * ⚠ Each leak's unit is declared rather than assumed — a percentage, a span in
 * minutes and a dollars-per-trade figure all arrive as bare numbers, and
 * printing "23" for a 23-minute hold gap beside "23" for 23% would be two
 * different facts wearing one label.
 */
function rateText(r: RankedLeak): string {
  const v = r.rate.value;
  if (v === null) return '—';
  if (r.unitLabel === 'pct') return `${num(v, 0)}%`;
  if (r.unitLabel === 'min') return `${num(v, 0)} min`;
  return money(v);
}
